import React, { useEffect, useMemo, useRef, useState } from 'react';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import steamNegative from '@op/assets/svg/steam_negative.svg';
import { cloneDeep } from 'lodash-es';
import mentionsSentimentOption from './mentionsSentimentOption';
import mentionsOption from './mentionsOption';
import sentimentOption from './sentimentOption';
import { useIntl } from 'umi';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { AUString } from '@/pages/opinion/utils';
import { setIntervalValue } from '@/pages/opinion/utils/chart';
import { NO_DATE } from '@op/constants';
import './chart.scss';
import { sentimentBrandHealthLegend } from './commonOption';
import { dealwithBrandHealth } from '@op/utils/value';

export default function Chart(props: any) {
  const {
    dataList,
    filter,
    curSubFilter,
    curFilter,
    subData,
    height = 300,
    subTitle,
    leftTitle,
    rightTitle,
    isMobile,
    isNoData,
  } = props;

  const showType =
    curSubFilter === 'DAU' ? AUString(filter?.date_type) : subTitle;
  const { formatMessage } = useIntl();

  const eChartsRef = useRef(null as any);
  const eChartsOptionsRef = useRef(null as any);

  const { date_type: dateType } = filter;

  const [option, setOption] = useState<any>(
    mentionsSentimentOption(dateType, formatMessage),
  );

  const getShowTotal = (dataList: any) => {
    // 小屏1366px且柱子数大于10，或者柱子数大于16根，不显示汇总值
    const offsetWidth: number = eChartsRef?.current
      ?.getEchartsInstance?.()
      ?.getWidth();
    return (offsetWidth <= 636 && dataList.length > 10) || dataList.length > 16;
  };

  const getLineChange = (option: any) => {
    const list = subData[curSubFilter];
    let blue: any = [];
    let orange: any = [];
    let predictArr: any = [];

    list?.map((item: any) => {
      predictArr.push(item.predict);
    });

    const legendMap: any = {
      1: [
        formatMessage({ id: 'Positive' }),
        formatMessage({ id: 'Neutral' }),
        formatMessage({ id: 'Negative' }),
      ],
      2: sentimentBrandHealthLegend(formatMessage),
    };

    const legendData = [...(legendMap[curFilter] || [])];

    if (isMobile) {
      let title = [];
      let bBol = predictArr.indexOf(0) !== -1;
      let oBol = predictArr.indexOf(1) !== -1;
      if (bBol && !oBol) {
        title.push('Sensor Tower');
      } else if (oBol && !bBol) {
        title.push('Predicted');
      } else {
        title.push('Sensor Tower', 'Predicted');
      }
      title.forEach((item) => {
        legendData.push(`${item} ${showType}`);
      });
    } else {
      legendData.push(showType);
    }

    option.legend.data = legendData;

    list?.map((item: any, index: number) => {
      if (predictArr[index] != 1) {
        // 当天没有补齐数据
        blue.push(item);
        orange.push('-');
      } else {
        // 当天有补齐数据
        // 前一天或者后一天都是补齐数据，countA 才填 '-'
        // 两条线才能连上
        if (index !== 0 && index !== list.length) {
          // 当前不是第一天或者最后一天
          if (predictArr[index - 1] && predictArr[index] && item.predict != 0) {
            blue.push('-');
          } else {
            blue.push(item);
          }
        } else if (index === 0 && predictArr[index]) {
          // 当前是第一天
          if (predictArr[index + 1] && item.predict != 0) {
            blue.push('-');
          } else {
            blue.push(item);
          }
        } else if (index === list.length && predictArr[index]) {
          // 当前是最后一天
          if (predictArr[index - 1] && item.predict != 0) {
            blue.push('-');
          } else {
            blue.push(item);
          }
        }
        orange.push(item);
      }
    });

    if (isMobile) {
      blue = blue.map((item: any) => {
        if (typeof item === 'object') {
          return item.value === 0 ? { ...item, value: null } : item;
        }
        return item;
      });
      orange = orange.map((item: any) => {
        if (typeof item === 'object') {
          return item.value === 0 ? { ...item, value: null } : item;
        }
        return item;
      });
      if (curFilter == '1') {
        option.series[4].data = blue;
        option.series[5].data = orange;
      } else {
        option.series[2].data = blue;
        option.series[3].data = orange;
      }
    } else {
      const key = curFilter == '1' ? 6 : 4;
      option.series[key].data = blue;
    }

    return getxAxis(list, option);
  };

  function getxAxis(list: any, option: any) {
    const time: any = [];
    if (list) {
      list?.map((item: any) => {
        time.push(item.time);
      });
    }
    option.xAxis[0].data = time;
  }

  function getBrandHealthValue(data: any) {
    const value: any = dealwithBrandHealth(data);
    return {
      value: value?.brand_health,
      flag: value?.brand_health === null,
    };
  }

  useEffect(() => {
    function getOtherData(options: Array<any>) {
      // 获取左侧轴的所有
      const listValue = options.map((itemSeries) => {
        const totalData = itemSeries.data.map((item: any) => {
          if (typeof item === 'string' || item === '-') return 0;
          if (typeof item === 'number') return item;
          return item.value;
        });
        return totalData;
      });

      return listValue.flat(3);
    }
    const setMentionsSentimentData = () => {
      if (dataList) {
        const avg: any = [];
        const brand_health: any = [];
        const negative: any = [];
        const neutraul: any = [];
        const positive: any = [];
        const time: any = [];

        const hideTotal = getShowTotal(dataList);
        const newOption = cloneDeep(
          mentionsSentimentOption(dateType, formatMessage, !hideTotal),
        );

        dataList.map((item: any) => {
          avg.push(item.sentiment_avg || '0.00');
          brand_health.push(getBrandHealthValue(item));
          negative.push(item.negative_mention);
          neutraul.push(item.neutral_mention);
          positive.push(item.positive_mention);
          time.push(item.time);
        });

        newOption.xAxis[0].data = time;
        // TODO 补充 total 数据
        newOption.series[0].data = avg;
        newOption.series[1].data = brand_health;
        newOption.series[2].data = dataList.map((m: any) => m?.mention);
        newOption.series[3].data = positive;
        newOption.series[4].data = neutraul;
        newOption.series[5].data = negative;
        setIntervalValue(newOption.series[2].data, newOption.yAxis[0]);
        setOption(newOption);
      }
    };
    const setMentionsData = () => {
      if (dataList) {
        const negative: any = [];
        const neutraul: any = [];
        const positive: any = [];
        const hideTotal = getShowTotal(dataList);
        let newOption = cloneDeep(
          mentionsOption(showType, formatMessage, dataList, filter, !hideTotal),
        );

        newOption.series[0].data = dataList.map((m: any) => m?.mention);

        dataList.map((item: any) => {
          negative.push(item.negative_mention);
          neutraul.push(item.neutral_mention);
          positive.push(item.positive_mention);
        });

        newOption.series[1].data = positive;
        newOption.series[2].data = neutraul;
        newOption.series[3].data = negative;

        getLineChange(newOption);
        // const listValue = getOtherData(newOption.series.slice(4));
        // setIntervalValue(newOption.series[0].data, newOption.yAxis[0]);
        // setIntervalValue(listValue, newOption.yAxis[1]);

        setOption(newOption);
      }
    };
    const setSentimentData = () => {
      if (dataList) {
        const avg: any = [];
        const brand_health: any = [];

        const newOption = cloneDeep(
          sentimentOption(showType, formatMessage, filter),
        );

        dataList.map((item: any) => {
          avg.push(item.sentiment_avg || '0.00');
          brand_health.push(getBrandHealthValue(item));
        });

        newOption.series[0].data = avg;
        newOption.series[1].data = brand_health;
        getLineChange(newOption);
        // const listValue = getOtherData(newOption.series.slice(2));
        // setIntervalValue(listValue, newOption.yAxis[1]);
        setOption(newOption);
      }
    };

    const map: any = {
      0: setMentionsSentimentData,
      1: setMentionsData,
      2: setSentimentData,
    };

    map[curFilter]();
  }, [
    curFilter,
    curSubFilter,
    dataList,
    subData,
    subTitle,
    isMobile,
    formatMessage,
  ]);

  const [rightLocalTitle, setRightLocalTitle] = useState(null);

  const onEvents = useMemo(() => {
    return {
      legendselectchanged: (params: any) => {
        if (eChartsOptionsRef.current) {
          const { name, selected } = params;
          const sentiment = formatMessage({ id: 'Sentiment' }),
            brandHealth = formatMessage({ id: 'Brand Health' });
          if ([sentiment, brandHealth].includes(name) && selected[name]) {
            const deleteKey = name === sentiment ? brandHealth : sentiment;
            setRightLocalTitle(name);
            eChartsOptionsRef.current.dispatchAction({
              type: 'legendUnSelect',
              name: deleteKey,
            });
          }
        }
      },
    };
  }, [formatMessage]);
  return (
    <EChartCommon
      ref={eChartsRef}
      options={option}
      leftTitle={curFilter === '2' ? rightLocalTitle || leftTitle : leftTitle}
      rightTitle={
        curFilter === '0' ? rightLocalTitle || rightTitle : rightTitle
      }
      height={height}
      empty={isNoData}
      onChartReady={(value) => {
        eChartsOptionsRef.current = value;
      }}
      optionsProps={{ type: curFilter === 2 ? 'line' : 'bar' }}
      eventsDict={onEvents}
    ></EChartCommon>
  );
}

/**
 * TODO: 其他系统使用
 * 获取浮窗中点赞、踩的图标和文字中英文
 * @param name                 名称，用于判断是否使用小图标
 * @param formatMessage        翻译方法
 * @param replaceName          判断是否需要替换名称
 * @param beforeItemClassName  是否需要在前面多添加一个带有该class的span
 * @returns
 */
export function getPositiveNegativeIconName(
  name: string,
  formatMessage: any,
  replaceName?: string,
  beforeItemClassName?: string,
) {
  const icon = name.includes('Positive')
    ? steamPositive
    : name.includes('Negative')
    ? steamNegative
    : null;
  const newName = formatMessage({ id: replaceName || name || NO_DATE.EMPTY });
  if (icon === null) return newName;
  let text = `<span><img src=${icon} style="width: 18px;height: 18px; vertical-align: middle;margin-right:4px" />${newName}</span>`;

  if (beforeItemClassName) {
    text = `<span class="${beforeItemClassName}"/>${text}`;
  }

  return text;
}
